import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Collapse,
  TextField,
  Divider,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

const SpecialRegimeCard = ({
  enabled,
  onToggle,
  selectedRegime,
  onConfigChange,
  initialData,
}) => {
  // Estado inicial das configurações
  const defaultConfigs = React.useMemo(
    () => ({
      vendasDentroEstado:
        "Para vendas dentro do mesmo estado, será aplicado o sistema de débito/crédito de ICMS",
      icms_nacional: initialData?.icms_nacional?.toString() || "0",
      icms_importado: initialData?.icms_importado?.toString() || "0",
    }),
    [initialData]
  );

  // Carrega as configurações específicas do regime do localStorage
  const [configs, setConfigs] = useState(() => {
    const savedConfig = localStorage.getItem(
      `special_regime_config_${selectedRegime}`
    );
    return savedConfig ? JSON.parse(savedConfig) : defaultConfigs;
  });

  // Atualiza as configurações quando dados chegam ou regime muda
  useEffect(() => {
    if (initialData) {
      const savedConfig = localStorage.getItem(
        `special_regime_config_${selectedRegime}`
      );
      const baseConfig = savedConfig ? JSON.parse(savedConfig) : {};
      setConfigs({
        ...defaultConfigs,
        ...baseConfig,
        // Prioriza dados do backend se existirem
        icms_nacional:
          initialData.icms_nacional?.toString() ||
          baseConfig.icms_nacional ||
          "0",
        icms_importado:
          initialData.icms_importado?.toString() ||
          baseConfig.icms_importado ||
          "0",
      });
    } else {
      // Se não há dados do backend, carrega do localStorage
      const savedConfig = localStorage.getItem(
        `special_regime_config_${selectedRegime}`
      );
      setConfigs(savedConfig ? JSON.parse(savedConfig) : { ...defaultConfigs });
    }
  }, [selectedRegime, defaultConfigs, initialData]);

  // Salva as configurações no localStorage quando mudam
  useEffect(() => {
    if (enabled) {
      localStorage.setItem(
        `special_regime_config_${selectedRegime}`,
        JSON.stringify(configs)
      );
    }
  }, [configs, enabled, selectedRegime]);
  const handleConfigChange = (field, value) => {
    setConfigs((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Notifica o componente pai sobre a alteração
    onConfigChange && onConfigChange();
  };
  const renderICMSConfig = () => {
    if (selectedRegime !== "LUCRO_REAL" && selectedRegime !== "LUCRO_PRESUMIDO")
      return null;

    return (
      <Box sx={{ mt: 3 }}>
        {/* ICMS Dentro do Estado */}
        <Box
          sx={{
            mb: 3,
            p: 3,
            bgcolor: "#fff3e0",
            borderRadius: 2,
            border: "1px solid #ffcc80",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 1, color: "#1a1a1a" }}
          >
            Vendas Dentro do Estado
          </Typography>
          <Typography variant="caption" sx={{ color: "#666", lineHeight: 1.4 }}>
            Para vendas dentro do mesmo estado, será aplicado o sistema de
            débito/crédito de ICMS
          </Typography>
        </Box>

        {/* ICMS Fora do Estado */}
        <Box
          sx={{
            p: 3,
            bgcolor: "#f8f9fa",
            borderRadius: 2,
            border: "1px solid #e8eaed",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 2, color: "#1a1a1a" }}
          >
            Vendas Fora do Estado
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              fullWidth
              label="% ICMS - Produtos Nacionais"
              type="number"
              size="small"
              value={configs.icms_nacional}
              onChange={(e) =>
                handleConfigChange("icms_nacional", e.target.value)
              }
              placeholder="Ex: 12"
              InputProps={{
                endAdornment: (
                  <Typography
                    variant="caption"
                    sx={{ color: "#ff7043", fontWeight: 600 }}
                  >
                    %
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#ff7043",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ff7043",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#ff7043",
                },
              }}
              helperText="Porcentagem de ICMS para produtos nacionais vendidos fora do estado"
            />
            <TextField
              fullWidth
              label="% ICMS - Produtos Importados"
              type="number"
              size="small"
              value={configs.icms_importado}
              onChange={(e) =>
                handleConfigChange("icms_importado", e.target.value)
              }
              placeholder="Ex: 4"
              InputProps={{
                endAdornment: (
                  <Typography
                    variant="caption"
                    sx={{ color: "#ff7043", fontWeight: 600 }}
                  >
                    %
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#ff7043",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ff7043",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#ff7043",
                },
              }}
              helperText="Porcentagem de ICMS para produtos importados vendidos fora do estado"
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Card
        sx={{
          border: enabled ? "3px solid #ff7043" : "1px solid #e8eaed",
          bgcolor: enabled ? "#ff704308" : "white",
          borderRadius: 3,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: enabled
            ? "0 8px 24px rgba(255, 112, 67, 0.15), 0 4px 12px rgba(0,0,0,0.1)"
            : "0 2px 8px rgba(0,0,0,0.08)",
          "&::before": enabled
            ? {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "linear-gradient(90deg, #ff7043, #ff904380)",
              }
            : {},
          position: "relative",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "#ff704315",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <StarIcon sx={{ color: "#ff7043", fontSize: 28 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: enabled ? "#ff7043" : "#1a1a1a",
                    fontSize: "1.1rem",
                  }}
                >
                  Regime Especial
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "0.9rem" }}
                >
                  Configurações especiais para situações específicas
                </Typography>
              </Box>
            </Box>{" "}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => onToggle(e.target.checked)}
                    disabled={selectedRegime === "SIMPLES_NACIONAL"}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#ff7043",
                        "&:hover": {
                          backgroundColor: "rgba(255, 112, 67, 0.08)",
                        },
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#ff7043",
                        },
                    }}
                  />
                }
                label={enabled ? "Ativo" : "Inativo"}
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    fontWeight: 600,
                    color: enabled ? "#ff7043" : "#666",
                    fontSize: "0.9rem",
                  },
                }}
              />
              {selectedRegime === "SIMPLES_NACIONAL" && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "error.main",
                    mt: 0.5,
                  }}
                >
                  Regime especial não pode ser aplicado ao Simples Nacional
                </Typography>
              )}
            </Box>
          </Box>

          <Collapse in={enabled}>
            <Divider sx={{ mb: 3 }} />
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: "#ff7043",
                fontSize: "1rem",
              }}
            >
              Configurações Especiais
            </Typography>{" "}
            {renderICMSConfig()}
            <Box
              sx={{
                mt: 3,
                p: 3,
                bgcolor: "#fff3e0",
                borderRadius: 2,
                border: "1px solid #ffcc80",
                borderLeft: "4px solid #ff7043",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#1a1a1a", lineHeight: 1.5 }}
              >
                <strong style={{ color: "#ff7043" }}>Importante:</strong> O
                regime especial deve ser utilizado apenas em casos específicos
                autorizados pela Receita Federal. Consulte seu contador antes de
                ativar esta opção.
              </Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SpecialRegimeCard;
